export type NearbyWeatherStationQueryType =
  | 'swell'
  | 'tides'
  | 'general'
  | 'both';

export interface NearbyWeatherStationApi {
  name: string;
  latitude: number;
  longitude: number;
  region?: string;
  state?: string;
  distance?: number;
  distanceUnits?: string;
  hasSwell?: boolean;
  hasTides?: boolean;
}

export interface ReferenceWeatherStationApi {
  name?: string;
  latitude?: number;
  longitude?: number;
  distance?: number;
  distanceUnits?: string;
}

export interface NearbyWeatherStationOption {
  latitude: number;
  longitude: number;
  name: string;
  shortName: string;
  distanceKm: number | null;
  description: string;
}

export interface NearbyWeatherStationsApiResponse {
  referenceWeatherStation?: ReferenceWeatherStationApi;
  nearbyWeatherStations?: NearbyWeatherStationApi[];
}

export const MAX_NEARBY_WEATHER_STATIONS = 3;

export const getReferenceLocationName = (
  referenceWeatherStation?: ReferenceWeatherStationApi | null,
): string | null => {
  if (!referenceWeatherStation) {
    return null;
  }

  if (referenceWeatherStation.name) {
    return referenceWeatherStation.name;
  }

  if (
    referenceWeatherStation.latitude != null &&
    referenceWeatherStation.longitude != null
  ) {
    return `[${referenceWeatherStation.latitude}/${referenceWeatherStation.longitude}]`;
  }

  return null;
};

export const formatNearbyWeatherStationOptions = (
  nearbyWeatherStations: NearbyWeatherStationApi[] | undefined,
  referenceWeatherStation?: ReferenceWeatherStationApi | null,
): NearbyWeatherStationOption[] => {
  if (!nearbyWeatherStations?.length) {
    return [];
  }

  const referenceLocationName = getReferenceLocationName(
    referenceWeatherStation,
  );

  return [...nearbyWeatherStations]
    .sort((a, b) => (a.distance ?? 0) - (b.distance ?? 0))
    .slice(0, MAX_NEARBY_WEATHER_STATIONS)
    .map((station) => {
      const locationName = [station.name, station.region, station.state]
        .filter(Boolean)
        .join(', ');

      let locationDescription = '';
      if (station.distance != null && referenceLocationName) {
        const distanceKilometres = (station.distance / 1000).toFixed(1);
        locationDescription = `${distanceKilometres}kms away from ${referenceLocationName}`;
      }

      const latitude = Number(station.latitude);
      const longitude = Number(station.longitude);
      const distanceKm =
        station.distance != null ? station.distance / 1000 : null;

      return {
        latitude,
        longitude,
        name: locationName,
        shortName: station.name,
        distanceKm,
        description: locationDescription,
      };
    })
    .filter(
      (station) =>
        Number.isFinite(station.latitude) &&
        Number.isFinite(station.longitude) &&
        station.name.length > 0,
    );
};
